import {
  Typography,
  RadioGroup,
  RadioGroupItem,
  Label,
  Input,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import type { ThresholdEditorProps } from "../types";

interface ThresholdEditorComponentProps extends Omit<ThresholdEditorProps, "value" | "displayValue" | "onValueChange"> {
  onValidate: (value: string, type: "amount" | "percentage") => boolean;
}

const ThresholdEditor = ({
  thresholdType,
  thresholdValue,
  isAbsoluteValue,
  unit,
  validationError,
  onThresholdTypeChange,
  onThresholdValueChange,
  onValidationError,
  onValidate,
}: ThresholdEditorComponentProps) => {
  const handleValueChange = (val: string, type: "amount" | "percentage") => {
    onThresholdValueChange(val);
    if (val) {
      onValidate(val, type);
    } else {
      onValidationError("");
    }
  };

  return (
    <div className="al-space-y-3">
      <Typography variant="body" weight="medium" className="al-text-sm">
        Enter Threshold Value
      </Typography>
      <RadioGroup
        value={thresholdType}
        onValueChange={(val: string) => {
          onThresholdTypeChange(val as "amount" | "percentage");
          onValidationError("");
        }}
        className="al-space-y-3"
      >
        <div className={cn(
          "al-border al-rounded-lg al-p-3",
          thresholdType === "amount" && "al-border-blue-500 al-bg-blue-50"
        )}>
          <Stack direction="row" gap="sm" align="center" className="al-mb-2">
            <RadioGroupItem value="amount" id="amount" />
            <Label htmlFor="amount" className="al-cursor-pointer al-text-sm al-font-medium">
              Absolute Number {unit && `(${unit})`}
            </Label>
          </Stack>
          <Input
            type="number"
            placeholder={unit ? `e.g., 3000 ${unit}` : "e.g., 3000"}
            value={thresholdType === "amount" ? thresholdValue : ""}
            onChange={(e) => handleValueChange(e.target.value, "amount")}
            disabled={thresholdType !== "amount"}
            className={cn(
              "al-mt-1",
              validationError && thresholdType === "amount" && "al-border-red-500"
            )}
          />
        </div>

        {!isAbsoluteValue && (
          <>
            <div className="al-text-center al-text-gray-500 al-text-xs">OR</div>

            <div className={cn(
              "al-border al-rounded-lg al-p-3",
              thresholdType === "percentage" && "al-border-blue-500 al-bg-blue-50"
            )}>
              <Stack direction="row" gap="sm" align="center" className="al-mb-2">
                <RadioGroupItem value="percentage" id="percentage" />
                <Label htmlFor="percentage" className="al-cursor-pointer al-text-sm al-font-medium">
                  Percentage (%)
                </Label>
              </Stack>
              <Input
                type="number"
                placeholder="e.g., 5"
                min="0"
                max="100"
                step="0.1"
                value={thresholdType === "percentage" ? thresholdValue : ""}
                onChange={(e) => handleValueChange(e.target.value, "percentage")}
                disabled={thresholdType !== "percentage"}
                className={cn(
                  "al-mt-1",
                  validationError && thresholdType === "percentage" && "al-border-red-500"
                )}
              />
            </div>
          </>
        )}
      </RadioGroup>
      {validationError && (
        <Typography variant="body" size="xs" className="al-text-red-600 al-text-xs">
          {validationError}
        </Typography>
      )}
    </div>
  );
};

export default ThresholdEditor;
